
import { useState, useEffect, useCallback, useRef } from 'react';
import { 
    subscribeToGenerationJobs, 
    addGenerationJob, 
    updateGenerationJob, 
    fetchBaseProblemsPaginated, 
    fetchGeneratedProblemsPaginated, 
    fetchTrashedProblemsPaginated,
    addGeneratedProblems,
    addMultipleBaseProblems,
    deleteBaseProblem, 
    deleteGeneratedProblem,
    deleteMultipleBaseProblems,
    deleteMultipleGeneratedProblems,
    deleteGenerationJob,
    restoreBaseProblem, 
    restoreGeneratedProblem,
    permanentlyDeleteBaseProblem, 
    permanentlyDeleteGeneratedProblem, 
    permanentlyDeleteAllTrashed,
    updateGeneratedProblem,
    updateBaseProblem,
    subscribeToWorksheets,
    addWorksheet,
    updateWorksheet,
    deleteWorksheet,
    subscribeToFolderStructure,
    saveFolderStructure,
    renameFolder,
    deleteProblemsByFolderPath,
    getNewGenerationJobId,
    createGenerationJobWithId,
    updateProblemOrder,
    initializeOrderIndices,
    syncFolderStructure,
    incrementUserStat
} from '../services/firebaseService';
import { 
    generateModifiedProblem, 
    generateCreativeProblem, 
    generateExplanationForProblem, 
    filterProblemsWithAI,
    fixMathInProblem
} from '../services/geminiService';
import { processImportJob } from '../services/processingService';
import type { 
    BaseProblem, 
    GeneratedProblem, 
    GenerationJob, 
    Recipe, 
    ExplanationGuidelines, 
    ProblemParts, 
    Worksheet,
    ModificationLevel,
    GenerationModel,
    ManualCrop,
    NavSelection
} from '../types';
import toast from 'react-hot-toast';

// Helper to remove undefined values for Firestore
const sanitizeForFirestore = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj.map(v => sanitizeForFirestore(v));
    } else if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
            const value = obj[key];
            if (value !== undefined) {
                acc[key] = sanitizeForFirestore(value);
            }
            return acc;
        }, {} as any);
    }
    return obj;
};

// Maximum concurrent jobs to prevent browser/API overload
const MAX_CONCURRENT_JOBS = 3;

export const useProblemManager = (
    userId: string | undefined, 
    recipe: Recipe, 
    guidelines: ExplanationGuidelines
) => {
    const [baseProblems, setBaseProblems] = useState<BaseProblem[]>([]);
    const [problemHistory, setProblemHistory] = useState<GeneratedProblem[]>([]);
    const [generationJobs, setGenerationJobs] = useState<GenerationJob[]>([]);
    const [trashedProblems, setTrashedProblems] = useState<(BaseProblem | GeneratedProblem)[]>([]);
    const [worksheets, setWorksheets] = useState<Worksheet[]>([]);
    const [allFolderPaths, setAllFolderPaths] = useState<string[]>([]);
    const [newContentFolders, setNewContentFolders] = useState<Set<string>>(new Set());

    // Pagination States
    const [lastBaseDoc, setLastBaseDoc] = useState<any>(null);
    const [hasNextBasePage, setHasNextBasePage] = useState(true);
    const [isFetchingNextBase, setIsFetchingNextBase] = useState(false);

    const [lastGeneratedDoc, setLastGeneratedDoc] = useState<any>(null);
    const [hasNextGeneratedPage, setHasNextGeneratedPage] = useState(true);
    const [isFetchingNextGenerated, setIsFetchingNextGenerated] = useState(false);

    const [lastTrashedDoc, setLastTrashedDoc] = useState<any>(null);
    const [hasNextTrashedPage, setHasNextTrashedPage] = useState(true);
    const [isFetchingNextTrashed, setIsFetchingNextTrashed] = useState(false);

    // Refs for job processing
    const processingJobIds = useRef<Set<string>>(new Set());
    const fileCache = useRef<Map<string, File[]>>(new Map());
    
    // Local ticker to force re-evaluation of the queue
    const [queueTick, setQueueTick] = useState(0);

    // --- Subscriptions ---
    useEffect(() => {
        if (!userId) {
            setGenerationJobs([]);
            setWorksheets([]);
            setAllFolderPaths([]);
            return;
        }

        const unsubJobs = subscribeToGenerationJobs(userId, (jobs) => {
            setGenerationJobs(jobs);
        });

        const unsubWorksheets = subscribeToWorksheets(userId, (ws) => {
            setWorksheets(ws);
        });

        const unsubFolders = subscribeToFolderStructure(userId, (structure) => {
            setAllFolderPaths(structure.paths);
        });

        // Initial fetch for problems
        fetchNextBaseProblems(true);
        fetchNextGeneratedProblems(true);
        fetchNextTrashed(true);

        return () => {
            unsubJobs();
            unsubWorksheets();
            unsubFolders();
        };
    }, [userId]);

    // --- Pagination Helpers ---
    const fetchNextBaseProblems = async (reset = false) => {
        if (!userId || (isFetchingNextBase && !reset) || (!hasNextBasePage && !reset)) return;
        setIsFetchingNextBase(true);
        try {
            const startAfterDoc = reset ? null : lastBaseDoc;
            const { problems, lastVisible } = await fetchBaseProblemsPaginated(userId, startAfterDoc);
            
            if (reset) {
                setBaseProblems(problems);
            } else {
                setBaseProblems(prev => {
                    const newProblems = problems.filter(p => !prev.some(existing => existing.id === p.id));
                    return [...prev, ...newProblems];
                });
            }
            setLastBaseDoc(lastVisible);
            setHasNextBasePage(!!lastVisible);
        } catch (e) {
            console.error("Failed to fetch base problems:", e);
        } finally {
            setIsFetchingNextBase(false);
        }
    };

    const fetchNextGeneratedProblems = async (reset = false) => {
        if (!userId || (isFetchingNextGenerated && !reset) || (!hasNextGeneratedPage && !reset)) return;
        setIsFetchingNextGenerated(true);
        try {
            const startAfterDoc = reset ? null : lastGeneratedDoc;
            const { problems, lastVisible } = await fetchGeneratedProblemsPaginated(userId, startAfterDoc);
            
            if (reset) {
                setProblemHistory(problems);
            } else {
                setProblemHistory(prev => {
                    const newProblems = problems.filter(p => !prev.some(existing => existing.id === p.id));
                    return [...prev, ...newProblems];
                });
            }
            setLastGeneratedDoc(lastVisible);
            setHasNextGeneratedPage(!!lastVisible);
        } catch (e) {
            console.error("Failed to fetch generated problems:", e);
        } finally {
            setIsFetchingNextGenerated(false);
        }
    };

    const fetchNextTrashed = async (reset = false) => {
        if (!userId || (isFetchingNextTrashed && !reset) || (!hasNextTrashedPage && !reset)) return;
        setIsFetchingNextTrashed(true);
        try {
            const startAfterDoc = reset ? null : lastTrashedDoc;
            const { problems, lastVisible } = await fetchTrashedProblemsPaginated(userId, startAfterDoc);
            
            if (reset) {
                setTrashedProblems(problems);
            } else {
                setTrashedProblems(prev => {
                    const newProblems = problems.filter(p => !prev.some(existing => existing.id === p.id));
                    return [...prev, ...newProblems];
                });
            }
            setLastTrashedDoc(lastVisible);
            setHasNextTrashedPage(!!lastVisible);
        } catch (e) {
            console.error("Failed to fetch trashed problems:", e);
        } finally {
            setIsFetchingNextTrashed(false);
        }
    };

    // --- Actions ---

    const onRequestGeneration = async (jobDetails: {
        baseProblem?: BaseProblem;
        modificationLevel: ModificationLevel;
        generationModel: GenerationModel;
        useThinkingMode?: boolean;
        curriculumPath: string;
        difficulty?: string;
        numberOfProblems: number;
        folder?: string;
    }) => {
        if (!userId) return;
        const { baseProblem, ...rest } = jobDetails;
        
        // Automatically register the target folder if it doesn't exist
        if (jobDetails.folder && !allFolderPaths.includes(jobDetails.folder)) {
            const newPaths = [...allFolderPaths, jobDetails.folder].sort();
            setAllFolderPaths(newPaths);
            saveFolderStructure(userId, { paths: newPaths }).catch(e => console.error("Failed to auto-register folder:", e));
        }

        const jobData: Omit<GenerationJob, 'id'> = {
            jobType: 'generation',
            baseProblemId: baseProblem?.id || null,
            baseProblemParts: baseProblem?.parts || null,
            baseProblemCroppedImage: baseProblem?.croppedImage || null,
            createdAt: new Date().toISOString(),
            status: 'pending',
            progress: { current: 0, total: 100, stage: '대기 중' },
            ...rest
        };

        await addGenerationJob(userId, sanitizeForFirestore(jobData));
    };

    const onQueueImport = async (files: File[], type: 'single' | 'dual', folderName: string, manualCrops?: ManualCrop[]) => {
        if (!userId) return;
        const jobId = getNewGenerationJobId(userId);
        if (!jobId) throw new Error("작업 ID 생성 실패");
        
        const jobDataRaw: Omit<GenerationJob, 'id'> = {
            jobType: 'import', 
            importSource: type, 
            fileNames: files.map(f => f.name),
            folder: folderName, 
            createdAt: new Date().toISOString(), 
            status: 'pending', 
            progress: { current: 0, total: 100, stage: '대기 중' },
            manualCrops
        };

        const jobData = sanitizeForFirestore(jobDataRaw);

        try {
            fileCache.current.set(jobId, files);
            await createGenerationJobWithId(userId, jobId, jobData);
        } catch (e) {
            console.error("Failed to queue import job:", e);
            fileCache.current.delete(jobId);
            throw e;
        }
    };

    // --- Job Processing Logic (Queue System) ---
    useEffect(() => {
        if (!userId) return;

        const activeJobCount = processingJobIds.current.size;
        const slotsAvailable = MAX_CONCURRENT_JOBS - activeJobCount;

        if (slotsAvailable <= 0) return;

        // Filter pending jobs that are NOT currently being processed locally
        const pendingJobs = generationJobs.filter(job => 
            job.status === 'pending' && !processingJobIds.current.has(job.id)
        );

        if (pendingJobs.length === 0) return;

        // Take only enough jobs to fill the slots
        const jobsToStart = pendingJobs.slice(0, slotsAvailable);

        const processJob = async (job: GenerationJob) => {
            // Double check to ensure no duplicate processing
            if (processingJobIds.current.has(job.id)) return;
            processingJobIds.current.add(job.id);

            try {
                await updateGenerationJob(userId, job.id, { status: 'processing', progress: { current: 0, total: 100, stage: '시작 중...' } });

                if (job.jobType === 'import') {
                    const files = fileCache.current.get(job.id);
                    if (!files) throw new Error("파일을 찾을 수 없습니다. 다시 시도해주세요.");

                    await processImportJob(
                        files, 
                        job.importSource || 'single', 
                        job.folder || '가져온 문제', 
                        guidelines,
                        (msg, progress) => updateGenerationJob(userId, job.id, { progress: { ...progress, stage: msg } }),
                        async (problems) => {
                            const ids = await addMultipleBaseProblems(userId, problems);
                            if (ids.length > 0) {
                                await incrementUserStat(userId, 'totalProblemsImported', ids.length);
                            }
                            
                            if (ids.length !== problems.length) {
                                throw new Error("데이터 저장 중 오류 발생: 일부 항목이 저장되지 않았습니다.");
                            }

                            // Update local state directly
                            const newBaseProblems = problems.map((p, i) => ({
                                ...p,
                                id: ids[i],
                                orderIndex: new Date(p.createdAt).getTime()
                            })) as BaseProblem[];

                            setBaseProblems(prev => {
                                const updated = [...newBaseProblems, ...prev];
                                return updated.sort((a, b) => (b.orderIndex || 0) - (a.orderIndex || 0));
                            });

                            if (job.folder) {
                                setNewContentFolders(prev => new Set(prev).add(job.folder!));
                            }
                        },
                        job.manualCrops
                    );
                    fileCache.current.delete(job.id);
                } else if (job.jobType === 'explanation') {
                    // Explanation Generation
                    if (!job.targetProblemId) throw new Error("대상 문제가 지정되지 않았습니다.");
                    const targetId = job.targetProblemId;
                    
                    const problem = [...baseProblems, ...problemHistory].find(p => p.id === targetId);
                    if (!problem) throw new Error("문제를 찾을 수 없습니다.");

                    await updateGenerationJob(userId, job.id, { progress: { current: 50, total: 100, stage: '해설 작성 중...' } });

                    const isGenerated = 'generatedParts' in problem;
                    const parts = isGenerated ? (problem as GeneratedProblem).generatedParts : (problem as BaseProblem).parts;
                    const model = job.generationModel || 'gemini-2.5-flash';

                    const newExplanation = await generateExplanationForProblem(parts, guidelines, model);
                    
                    if (isGenerated) {
                        await updateGeneratedProblem(userId, targetId, { explanation: newExplanation });
                        setProblemHistory(prev => prev.map(p => p.id === targetId ? { ...p, explanation: newExplanation } : p));
                    } else {
                        await updateBaseProblem(userId, targetId, { explanation: newExplanation });
                        setBaseProblems(prev => prev.map(p => p.id === targetId ? { ...p, explanation: newExplanation } : p));
                    }
                    await incrementUserStat(userId, 'explanationRegenerations', 1);

                } else if (job.jobType === 'fix') {
                    // Math Fix
                    if (!job.targetProblemId || !job.fixTarget) throw new Error("수식 교정 대상이 올바르지 않습니다.");
                    const targetId = job.targetProblemId;
                    const targetType = job.fixTarget;

                    const problem = [...baseProblems, ...problemHistory].find(p => p.id === targetId);
                    if (!problem) throw new Error("문제를 찾을 수 없습니다.");

                    await updateGenerationJob(userId, job.id, { progress: { current: 50, total: 100, stage: '수식 분석 중...' } });

                    const isGenerated = 'generatedParts' in problem;
                    const parts = isGenerated ? (problem as GeneratedProblem).generatedParts : (problem as BaseProblem).parts;
                    const explanation = problem.explanation;

                    const fixed = await fixMathInProblem(parts.lines, parts.choices || null, explanation || null, targetType);
                    
                    const newParts = { ...parts, lines: fixed.lines, choices: fixed.choices };
                    const newExplanation = fixed.explanation;

                    if (isGenerated) {
                        await updateGeneratedProblem(userId, targetId, { generatedParts: newParts, explanation: newExplanation });
                        setProblemHistory(prev => prev.map(p => p.id === targetId ? { ...p, generatedParts: newParts, explanation: newExplanation } : p));
                    } else {
                        await updateBaseProblem(userId, targetId, { parts: newParts, explanation: newExplanation });
                        setBaseProblems(prev => prev.map(p => p.id === targetId ? { ...p, parts: newParts, explanation: newExplanation } : p));
                    }
                    await incrementUserStat(userId, 'mathFixCount', 1);

                } else {
                    // Generation Job
                    await updateGenerationJob(userId, job.id, { progress: { current: 20, total: 100, stage: 'AI 생성 중...' } });
                    
                    let generatedResults: any[] = [];
                    
                    if (job.modificationLevel === 4) {
                        generatedResults = await generateCreativeProblem(
                            job.generationModel || 'gemini-2.5-pro',
                            recipe,
                            guidelines,
                            job.curriculumPath || '',
                            job.difficulty,
                            job.numberOfProblems || 1,
                            job.useThinkingMode
                        );
                        await incrementUserStat(userId, 'creativeProblemsGenerated', generatedResults.length);
                    } else if (job.baseProblemParts) {
                        const baseProblem: BaseProblem = {
                            id: job.baseProblemId!,
                            parts: job.baseProblemParts,
                            croppedImage: job.baseProblemCroppedImage,
                            createdAt: new Date().toISOString()
                        } as BaseProblem;

                        generatedResults = await generateModifiedProblem(
                            baseProblem,
                            job.modificationLevel || 1,
                            job.generationModel || 'gemini-2.5-flash',
                            recipe,
                            guidelines,
                            job.curriculumPath || '',
                            job.difficulty,
                            job.numberOfProblems || 1,
                            job.useThinkingMode
                        );
                        await incrementUserStat(userId, 'totalProblemsGenerated', generatedResults.length);
                    }

                    const problemsToSave = generatedResults.map(res => ({
                        baseProblemId: job.baseProblemId || null, // Explicit null
                        baseProblemParts: job.baseProblemParts || null, // Explicit null
                        baseProblemCroppedImage: job.baseProblemCroppedImage || null, // Explicit null
                        generatedParts: res.generatedParts,
                        modificationLevel: job.modificationLevel!,
                        generationModel: job.generationModel!,
                        createdAt: new Date().toISOString(),
                        explanation: res.explanation,
                        curriculumPath: job.curriculumPath || '',
                        difficulty: job.difficulty || null, // explicit null
                        folder: job.folder || null, // explicit null
                        coreIdeas: res.coreIdeas,
                        difficultyScore: res.difficultyScore,
                        metadataStatus: 'completed' as const
                    }));

                    const ids = await addGeneratedProblems(userId, sanitizeForFirestore(problemsToSave));
                    
                    if (ids.length !== problemsToSave.length) {
                        throw new Error("문제 저장 중 오류가 발생했습니다. 저장된 데이터가 생성된 데이터와 일치하지 않습니다.");
                    }

                    await updateGenerationJob(userId, job.id, { generatedProblemIds: ids });
                    
                    // Update local state directly to prevent delay or missing items
                    const newGeneratedProblems = problemsToSave.map((p, i) => ({
                        ...p,
                        id: ids[i],
                        orderIndex: new Date(p.createdAt).getTime()
                    })) as GeneratedProblem[];

                    setProblemHistory(prev => {
                        const updated = [...newGeneratedProblems, ...prev];
                        // Sort by orderIndex desc
                        return updated.sort((a, b) => (b.orderIndex || 0) - (a.orderIndex || 0));
                    });

                    if (job.folder) {
                        setNewContentFolders(prev => new Set(prev).add(job.folder!));
                    }
                }

                await updateGenerationJob(userId, job.id, { status: 'completed', progress: { current: 100, total: 100, stage: '완료' } });
            } catch (e: any) {
                console.error("Job failed:", e);
                await updateGenerationJob(userId, job.id, { status: 'failed', error: e.message });
            } finally {
                processingJobIds.current.delete(job.id);
                // Force re-check of queue to pick up next job
                setQueueTick(prev => prev + 1);
            }
        };

        jobsToStart.forEach(job => {
            processJob(job);
        });
    }, [userId, generationJobs, recipe, guidelines, queueTick]);

    // --- CRUD Operations ---

    const onAddMultipleBaseProblems = async (problems: Omit<BaseProblem, 'id' | 'duplicateOf' | 'deletedAt'>[]) => {
        if (!userId) return;
        const ids = await addMultipleBaseProblems(userId, problems);
        
        const newBaseProblems = problems.map((p, i) => ({
            ...p,
            id: ids[i],
            orderIndex: new Date(p.createdAt).getTime()
        })) as BaseProblem[];

        setBaseProblems(prev => [...newBaseProblems, ...prev]);
    };

    const onDeleteBaseProblem = async (id: string) => {
        if (!userId) return;
        await deleteBaseProblem(userId, id);
        setBaseProblems(prev => prev.filter(p => p.id !== id));
        fetchNextTrashed(true);
    };

    const onDeleteGeneratedProblem = async (id: string) => {
        if (!userId) return;
        await deleteGeneratedProblem(userId, id);
        setProblemHistory(prev => prev.filter(p => p.id !== id));
        fetchNextTrashed(true);
    };

    const onDeleteJob = async (id: string) => {
        if (!userId) return;
        await deleteGenerationJob(userId, id);
    };

    const onRestoreProblem = async (problem: BaseProblem | GeneratedProblem) => {
        if (!userId) return;
        if ('generatedParts' in problem) {
            await restoreGeneratedProblem(userId, problem.id);
            fetchNextGeneratedProblems(true);
        } else {
            await restoreBaseProblem(userId, problem.id);
            fetchNextBaseProblems(true);
        }
        fetchNextTrashed(true);
    };

    const onPermanentlyDeleteProblem = async (problem: BaseProblem | GeneratedProblem) => {
        if (!userId) return;
        if ('generatedParts' in problem) {
            await permanentlyDeleteGeneratedProblem(userId, problem.id);
        } else {
            await permanentlyDeleteBaseProblem(userId, problem.id);
        }
        fetchNextTrashed(true);
    };

    const onDeleteMultipleGeneratedProblems = async (ids: string[]) => {
        if (!userId) return;
        await deleteMultipleGeneratedProblems(userId, ids);
        setProblemHistory(prev => prev.filter(p => !ids.includes(p.id)));
        fetchNextTrashed(true);
    };

    const onDeleteMultipleBaseProblems = async (ids: string[]) => {
        if (!userId) return;
        await deleteMultipleBaseProblems(userId, ids);
        setBaseProblems(prev => prev.filter(p => !ids.includes(p.id)));
        fetchNextTrashed(true);
    };

    const onPermanentlyDeleteAllTrashed = async () => {
        if (!userId) return;
        await permanentlyDeleteAllTrashed(userId, trashedProblems);
        fetchNextTrashed(true);
    };

    // Refactored to use Jobs
    const onRegenerateExplanation = async (problemId: string, model: GenerationModel = 'gemini-2.5-flash') => {
        if (!userId) return;
        
        const problem = [...baseProblems, ...problemHistory].find(p => p.id === problemId);
        if (!problem) return;

        const isGenerated = 'generatedParts' in problem;
        
        // Optimistic update to UI
        if (isGenerated) {
            await updateGeneratedProblem(userId, problemId, { explanation: '__processing__' });
            setProblemHistory(prev => prev.map(p => p.id === problemId ? { ...p, explanation: '__processing__' } : p));
        } else {
            await updateBaseProblem(userId, problemId, { explanation: '__processing__' });
            setBaseProblems(prev => prev.map(p => p.id === problemId ? { ...p, explanation: '__processing__' } : p));
        }

        // Create Job
        const jobData: Omit<GenerationJob, 'id'> = {
            jobType: 'explanation',
            targetProblemId: problemId,
            generationModel: model,
            createdAt: new Date().toISOString(),
            status: 'pending',
            progress: { current: 0, total: 100, stage: '대기 중' }
        };

        await addGenerationJob(userId, sanitizeForFirestore(jobData));
    };

    const onCancelExplanationGeneration = async (problemId: string) => {
        if (!userId) return;
        // Find active job for this problem
        const activeJob = generationJobs.find(j => j.targetProblemId === problemId && j.jobType === 'explanation' && (j.status === 'pending' || j.status === 'processing'));
        if (activeJob) {
            await onCancelJob(activeJob.id);
        }
        // Revert UI state
        if (problemHistory.some(p => p.id === problemId)) fetchNextGeneratedProblems(true);
        if (baseProblems.some(p => p.id === problemId)) fetchNextBaseProblems(true);
    };

    const onUpdateProblemContent = async (problemId: string, isGenerated: boolean, newParts: ProblemParts, newExplanation: string | null) => {
        if (!userId) return;
        if (isGenerated) {
            await updateGeneratedProblem(userId, problemId, { generatedParts: newParts, explanation: newExplanation });
            setProblemHistory(prev => prev.map(p => p.id === problemId ? { ...p, generatedParts: newParts, explanation: newExplanation } : p));
        } else {
            await updateBaseProblem(userId, problemId, { parts: newParts, explanation: newExplanation });
            setBaseProblems(prev => prev.map(p => p.id === problemId ? { ...p, parts: newParts, explanation: newExplanation } : p));
        }
    };

    const onAddWorksheet = async (worksheet: Omit<Worksheet, 'id'>) => {
        if (!userId) return;
        await addWorksheet(userId, worksheet);
        await incrementUserStat(userId, 'worksheetsCreated', 1);
    };

    const onUpdateWorksheet = async (id: string, data: Partial<Omit<Worksheet, 'id'>>) => {
        if (!userId) return;
        await updateWorksheet(userId, id, data);
    };

    const onDeleteWorksheet = async (id: string) => {
        if (!userId) return;
        await deleteWorksheet(userId, id);
    };

    const onAddFolder = async (path: string) => {
        if (!userId) return;
        if (!allFolderPaths.includes(path)) {
            const newPaths = [...allFolderPaths, path].sort();
            setAllFolderPaths(newPaths);
            await saveFolderStructure(userId, { paths: newPaths });
        }
    };

    const onRenameFolder = async (oldPath: string, newPath: string) => {
        if (!userId) return;
        const newPaths = allFolderPaths.map(p => {
            if (p === oldPath) return newPath;
            if (p.startsWith(oldPath + '/')) return p.replace(oldPath + '/', newPath + '/');
            return p;
        }).sort();
        setAllFolderPaths(newPaths);
        await saveFolderStructure(userId, { paths: newPaths });

        await renameFolder(userId, 'baseProblemHistory', oldPath, newPath);
        await renameFolder(userId, 'problemHistory', oldPath, newPath);
        
        fetchNextBaseProblems(true);
        fetchNextGeneratedProblems(true);
    };

    const onDeleteFolder = async (path: string) => {
        if (!userId) return;
        await deleteProblemsByFolderPath(userId, 'baseProblemHistory', path);
        await deleteProblemsByFolderPath(userId, 'problemHistory', path);

        const newPaths = allFolderPaths.filter(p => p !== path && !p.startsWith(path + '/'));
        setAllFolderPaths(newPaths);
        await saveFolderStructure(userId, { paths: newPaths });

        fetchNextBaseProblems(true);
        fetchNextGeneratedProblems(true);
        fetchNextTrashed(true);
    };

    const onAiSearch = async (query: string, problems: (BaseProblem | GeneratedProblem)[]) => {
        return await filterProblemsWithAI(query, problems);
    };

    const onMoveProblem = async (problemId: string, isGenerated: boolean, newFolderPath: string) => {
        if (!userId) return;
        if (isGenerated) {
            await updateGeneratedProblem(userId, problemId, { folder: newFolderPath });
            setProblemHistory(prev => prev.map(p => p.id === problemId ? { ...p, folder: newFolderPath } : p));
        } else {
            await updateBaseProblem(userId, problemId, { folder: newFolderPath });
            setBaseProblems(prev => prev.map(p => p.id === problemId ? { ...p, folder: newFolderPath } : p));
        }
    };

    const onCancelJob = async (jobId: string) => {
        if (!userId) return;
        await updateGenerationJob(userId, jobId, { status: 'failed', error: '사용자에 의해 취소됨' });
        processingJobIds.current.delete(jobId);
        fileCache.current.delete(jobId);
        setQueueTick(prev => prev + 1);
    };

    const clearNewContentFolder = (path: string) => {
        setNewContentFolders(prev => {
            const next = new Set(prev);
            next.delete(path);
            return next;
        });
    };

    // Refactored to use Jobs
    const onFixProblemData = async (problemId: string, isGenerated: boolean, target: 'problem' | 'explanation') => {
        if (!userId) return;
        
        // Create Job
        const jobData: Omit<GenerationJob, 'id'> = {
            jobType: 'fix',
            targetProblemId: problemId,
            fixTarget: target,
            createdAt: new Date().toISOString(),
            status: 'pending',
            progress: { current: 0, total: 100, stage: '대기 중' }
        };

        await addGenerationJob(userId, sanitizeForFirestore(jobData));
    };

    const onToggleLock = async (problemId: string, isGenerated: boolean, newLockedState: boolean) => {
        if (!userId) return;
        if (isGenerated) {
            await updateGeneratedProblem(userId, problemId, { isLocked: newLockedState });
            setProblemHistory(prev => prev.map(p => p.id === problemId ? { ...p, isLocked: newLockedState } : p));
        } else {
            await updateBaseProblem(userId, problemId, { isLocked: newLockedState });
            setBaseProblems(prev => prev.map(p => p.id === problemId ? { ...p, isLocked: newLockedState } : p));
        }
    };

    const onUpdateProblemOrder = async (problemId: string, isGenerated: boolean, newOrderIndex: number) => {
        if (!userId) return;
        await updateProblemOrder(userId, problemId, isGenerated, newOrderIndex);
        
        if (isGenerated) {
            setProblemHistory(prev => prev.map(p => p.id === problemId ? { ...p, orderIndex: newOrderIndex } : p).sort((a, b) => (b.orderIndex ?? 0) - (a.orderIndex ?? 0)));
        } else {
            setBaseProblems(prev => prev.map(p => p.id === problemId ? { ...p, orderIndex: newOrderIndex } : p).sort((a, b) => (b.orderIndex ?? 0) - (a.orderIndex ?? 0)));
        }
    };

    const onInitializeOrderIndices = async () => {
        if (!userId) return;
        await initializeOrderIndices(userId);
        fetchNextBaseProblems(true);
        fetchNextGeneratedProblems(true);
    };

    const onSyncFolders = async () => {
        if (!userId) return;
        await syncFolderStructure(userId);
    };

    return {
        baseProblems,
        problemHistory,
        generationJobs,
        trashedProblems,
        worksheets,
        allFolderPaths,
        newContentFolders,
        clearNewContentFolder,
        
        fetchNextBaseProblems, hasNextBasePage, isFetchingNextBase,
        fetchNextGeneratedProblems, hasNextGeneratedPage, isFetchingNextGenerated,
        fetchNextTrashed, hasNextTrashedPage, isFetchingNextTrashed,

        onRequestGeneration,
        onQueueImport,
        onAddMultipleBaseProblems,
        onDeleteBaseProblem,
        onDeleteGeneratedProblem,
        onDeleteJob,
        onRestoreProblem,
        onPermanentlyDeleteProblem,
        onDeleteMultipleGeneratedProblems,
        onDeleteMultipleBaseProblems,
        onPermanentlyDeleteAllTrashed,
        onRegenerateExplanation,
        onCancelExplanationGeneration,
        onUpdateProblemContent,
        onAddWorksheet,
        onUpdateWorksheet,
        onDeleteWorksheet,
        onAddFolder,
        onRenameFolder,
        onDeleteFolder,
        onAiSearch,
        onMoveProblem,
        onCancelJob,
        onFixProblemData,
        onToggleLock,
        onUpdateProblemOrder,
        onInitializeOrderIndices,
        onSyncFolders
    };
};
